import { Size } from '@/hooks/useScreenSize';

// Style maps for sizes
const dialogContainerClassMap: Record<Size, string> = {
  sm: 'fixed top-0 left-0 right-0 h-[100vh] z-[99999] bg-gradients-background-section-pop-up-subtle-glow-pop-up backdrop-blur-[4px]',
  md: 'fixed top-0 left-0 right-0 h-[100vh] z-[99999] bg-gradients-background-section-pop-up-subtle-glow-pop-up backdrop-blur-[4px]',
  lg: 'fixed top-0 left-0 right-0 h-[100vh] z-[99999] bg-color-background-overlay-desktop-primary backdrop-blur-[4px]',
  xl: 'fixed top-0 left-0 right-0 h-[100vh] z-[99999] bg-color-background-overlay-desktop-primary backdrop-blur-[4px] ',
  xxl: 'fixed top-0 left-0 right-0 h-[100vh] z-[99999] bg-color-background-overlay-desktop-primary backdrop-blur-[4px] ',
};

const dialogContentClassMap: Record<Size, string> = {
  sm: 'h-[100vh] w-full flex flex-col overflow-hidden scrollbar-none scrollbar-hide border-solid border-[1px] border-gradients-border-popup-card-border ',
  md: 'h-[100vh] w-full flex flex-col overflow-hidden scrollbar-none scrollbar-hide border-solid border-[1px] border-gradients-border-popup-card-border ',
  lg: 'w-[768px] max-h-[80vh] m-auto flex flex-col overflow-hidden bg-gradients-background-section-pop-up-subtle-glow-pop-up border-gradients-border-popup-card-border gap-spacing-none shadow-cartBottomDialogShadow rounded-radius-2xl',
  xl: 'w-[768px] max-h-[80vh] m-auto flex flex-col overflow-hidden bg-gradients-background-section-pop-up-subtle-glow-pop-up border-gradients-border-popup-card-border gap-spacing-none shadow-cartBottomDialogShadow rounded-radius-2xl',
  xxl: 'w-[998px] max-h-[80vh] m-auto flex flex-col overflow-hidden bg-gradients-background-section-pop-up-subtle-glow-pop-up border-gradients-border-popup-card-border gap-spacing-none shadow-cartBottomDialogShadow rounded-radius-2xl',
};

const commonSpacingClassMap: Record<Size, string> = {
  sm: '',
  md: '',
  lg: 'pt-spacing-4xs pr-spacing-lg pb-spacing-10xl pl-spacing-lg',
  xl: 'pt-spacing-4xs pr-spacing-lg pb-spacing-10xl pl-spacing-lg',
  xxl: 'pt-spacing-3xs pr-spacing-3xl pb-spacing-10xl pl-spacing-3xl',
};

const commonForLRBSpacingClassMap: Record<Size, string> = {
  sm: 'pr-spacing-xs pl-spacing-xs pb-spacing-7xl',
  md: 'pr-spacing-3xl pl-spacing-3xl pb-spacing-9xl',
  lg: 'pr-spacing-7xl pl-spacing-7xl pb-spacing-10xl',
  xl: 'pr-spacing-7xl pl-spacing-7xl pb-spacing-10xl',
  xxl: 'pr-spacing-7xl pl-spacing-7xl pb-spacing-10xl',
};

const commonForLRSpacingClassMap: Record<Size, string> = {
  sm: 'pr-spacing-xs pl-spacing-xs',
  md: 'pr-spacing-3xl pl-spacing-3xl',
  lg: 'pr-spacing-7xl pl-spacing-7xl',
  xl: 'pr-spacing-7xl pl-spacing-7xl',
  xxl: 'pr-spacing-7xl pl-spacing-7xl',
};

const dialogContentPositionClassMap: Record<Size, string> = {
  sm: 'relative px-4 flex items-center w-full h-full',
  md: 'relative px-4 flex items-center w-full h-full',
  lg: 'relative px-0 flex items-start w-full h-full',
  xl: 'relative px-0 flex items-start w-full h-full',
  xxl: 'relative px-0 flex items-start w-full h-full',
};

const contentPostionClassMap: Record<Size, string> = {
  sm: 'flex items-center justify-center w-full h-full',
  md: 'flex items-center justify-center w-full h-full',
  lg: 'flex items-start justify-center w-full h-full',
  xl: 'flex items-start justify-center w-full h-full',
  xxl: 'flex items-start justify-center w-full h-full',
};

const descriptionClassMap: Record<Size, string> = {
  sm: 'font-Markpro text-base text-center text-lavender-gray',
  md: 'font-Markpro text-md text-center text-lavender-gray',
  lg: 'font-Markpro text-lg text-center text-lavender-gray',
  xl: 'font-Markpro text-lg text-center text-lavender-gray',
  xxl: 'font-Markpro font-normal text-[32px] text-center text-lavender-gray w-[935.6px] leading-[44px]',
};

const dialogCloseClassMap: Record<Size, string> = {
  sm: 'fixed right-4 top-4 z-[999]',
  md: 'fixed right-4 top-4 z-[999]',
  lg: 'absolute right-[32px] top-[32px] z-[999]',
  xl: 'absolute right-[32px] top-[32px] z-[999]',
  xxl: 'absolute right-[32px] top-[32px] z-[999]',
};

const iconClassMap: Record<Size, string> = {
  sm: '!w-[27px] !h-[27px]',
  md: '!w-[35.1px] !h-[35.1px]',
  lg: '!w-[45.63px] !h-[45.63px]',
  xl: '!w-[45.63px] !h-[45.63px]',
  xxl: '!w-[77.115px] !h-[77.115px]',
};

const stickyContainerClassMap: Record<Size, string> = {
  sm: 'relative z-[999] m-auto shrink-0',
  md: 'relative z-[999] m-auto shrink-0',
  lg: 'relative z-[999] m-auto shrink-0',
  xl: 'relative z-[999] m-auto shrink-0',
  xxl: 'relative z-[999] m-auto shrink-0',
};

const addressCheckerContentPositionClassMap: Record<Size, string> = {
  sm: 'min-w-full relative',
  md: 'min-w-full relative',
  lg: 'min-w-full relative',
  xl: 'min-w-full relative',
  xxl: 'min-w-full relative',
};

const iconContainerClassMap =
  'flex h-[91px] w-full justify-end px-spacing-spacing-32 items-center py-[8px]';

const notificationIconContainerClassMap: Record<Size, string> = {
  sm: 'fixed right-4 top-4 z-[999]',
  md: 'fixed right-4 top-4 z-[999]',
  lg: 'flex h-[109.6px] w-full cursor-pointer justify-end p-spacing-spacing-32 items-center',
  xl: 'flex h-[109.6px] w-full cursor-pointer justify-end p-spacing-spacing-32 items-center',
  xxl: 'flex h-[109.6px] w-full cursor-pointer justify-end p-spacing-spacing-32 items-center',
};

export {
  dialogContainerClassMap,
  dialogContentClassMap,
  dialogCloseClassMap,
  iconClassMap,
  dialogContentPositionClassMap,
  descriptionClassMap,
  contentPostionClassMap,
  stickyContainerClassMap,
  addressCheckerContentPositionClassMap,
  commonSpacingClassMap,
  commonForLRBSpacingClassMap,
  commonForLRSpacingClassMap,
  iconContainerClassMap,
  notificationIconContainerClassMap,
};
